// components/AudioPlayer.js
import React, { useRef, useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import recordImages from './recordImages';

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const AudioPlayerContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const RecordContainer = styled.div`
  position: relative;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: ${({ imageUrl }) => `url(${imageUrl}) center/cover`};
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $isPlaying }) => $isPlaying && css`
    animation: ${spin} 5s linear infinite;
  `}
`;

const Hole = styled.div`
  width: 50px;
  height: 50px;
  background-color: ${({ theme }) => theme.colors.secondary};
  border-radius: 50%;
  z-index: 1;
`;

const Overlay = styled.div`
  display: ${({ isLoading }) => (isLoading ? 'flex' : 'none')};
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const AudioElement = styled.audio`
  width: 100%;
  margin-top: 20px;
`;

const AudioPlayer = ({ src, isLoading, onEnded }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [imageUrl, setImageUrl] = useState(recordImages[0]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.load();
      audioRef.current.play().then(() => setIsPlaying(true));
    }
  }, [src]);

  useEffect(() => {
    const handlePause = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);

    if (audioRef.current) {
      audioRef.current.addEventListener('pause', handlePause);
      audioRef.current.addEventListener('play', handlePlay);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('pause', handlePause);
        audioRef.current.removeEventListener('play', handlePlay);
      }
    };
  }, [audioRef]);

  useEffect(() => {
    // Optionally change the image URL for each track or based on some logic
    setImageUrl(recordImages[Math.floor(Math.random() * recordImages.length)]);
  }, [src]);

  return (
    <AudioPlayerContainer>
      <Overlay isLoading={isLoading && !isPlaying}>Loading...</Overlay>
      <RecordContainer $isPlaying={isPlaying} imageUrl={imageUrl}>
        <Hole />
      </RecordContainer>
      <AudioElement ref={audioRef} controls src={src} onEnded={onEnded} />
    </AudioPlayerContainer>
  );
};

export default AudioPlayer;
